// RMA MPCI premium engine — the math only. Rate TABLES are passed in, so the exact same
// code prices a live 2027 quote and gets backtested against 2026 premiums the AIP actually
// billed. (If the backtest re-implemented the math, a bug could hide in both copies.)
//
// Implements RMA's P11-1 Premium Calculation exhibit (M13 Handbook). Section numbers below
// refer to it — it is the authoritative spec, and the place to go for any premium question.
//
// TREND ADJUSTMENT DOES NOT CHANGE COVERAGE. It raises the APPROVED APH, which raises the
// guarantee (approved yield × the level the grower ELECTED). He elected 80%, he is covered
// at 80%. The guarantee below is built off `cl` — never off `eff`.
// "Effective coverage level" is an INTERNAL PREMIUM-RATING index: the TA-boosted guarantee
// is larger relative to the RATE yield the base rate is built on, so RMA charges for that
// extra guarantee by looking the rate factors up higher. A premium knob, not a coverage knob.
//
// THREE yields, not interchangeable (all three live on the AIP's P15 record):
//   aph           = APPROVED yield (f36) — the guarantee/liability. TA/YE-boosted.
//   rateYield     = RATE yield     (f34) — the base rate            (§3).
//   adjustedYield = ADJUSTED yield (f43) — the effective cov. level (§13). APH w/o TA/YE, WITH YA.
// rate == adjusted on any policy WITHOUT YA — which is why they look like one number until
// a YA grower shows up. Defaults chain, so a caller holding only the approved yield still
// gets a correct un-adjusted quote.

export const R = (x, n) => { const p = 10 ** n; return Math.round(x * p) / p; };
const comboIdx = r => Math.max(0, Math.min(3900, Math.round(r * 10000) - 100));

export function discFactor(unit, cl, acres, tbl) {
  if (unit === "OU") return 1.0;
  const row = (tbl || []).find(d => Math.abs(d.cl - cl) < 1e-6 && acres >= d.lo && acres <= d.hi);
  return row ? (unit === "EU" ? row.eu : row.bu) : 1.0;
}
// Bracket the effective level on the ADM's 5% grid; above the top level, extrapolate off
// the top two (§14/§15/§16 all say the same thing).
function effBounds(levels, eff) {
  const max = levels[levels.length - 1], second = levels[levels.length - 2];
  if (eff > max) return { fl: max, lo: second, up: max };
  for (const L of levels) if (Math.abs(eff - L) < 1e-9) return { fl: L, lo: L, up: L };
  for (let i = 0; i < levels.length - 1; i++) if (eff > levels[i] && eff < levels[i + 1]) return { fl: levels[i], lo: levels[i], up: levels[i + 1] };
  return { fl: levels[0], lo: levels[0], up: levels[1] };
}
const effInterp = (base, up, lo, eff, fl, dp) => R(base + (up - lo) * (eff - fl) * 20, dp);   // ×20 == ÷0.05 grid

// ── §14: WHICH rate-differential formula ────────────────────────────────────────────────
// RMA P11-1 RY2027 §14 prints TWO formulas, and they differ only above an effective level
// of 0.85:
//   "When the Insurance Option Code List contains Options QL, YE, or YC":
//        RDF = (1 + ROUND(MIN((MAX(0.85, eff) − 0.85) / 0.15, 1)^3, 7) × 0.05) × <linear>
//   "When Trend Adjustment Option TA is elected alone (excludes YC and YE)":
//        RDF = <linear>                                    — no cubic surcharge
// The engine used to apply the surcharge to everybody (BUG-095).
//
// THE LIST IS THE ONE ON THE APH DATABASE BEING RATED — the P11 record's own option list,
// equivalently P15 f[38] — NOT the coverage's ELECTION list (P14 f[37]). Measured against the
// AIP's own billed base premium rate (P11 f110) on every §20 wheat unit in NAU's CY2026 file:
//   Genesee   P11 PF,TA,YA,QL → surcharge     f110 0.1294815   exact WITH it
//   Steuben   P11 PF,TA,YE    → surcharge     f110 0.14591969  exact WITH it
//   Orleans   P11 PF,QL,TA,YA → surcharge     f110 0.16168528  exact WITH it
//   Livingston P11 PF,TA,YA   → TA alone      f110 0.15001637  exact WITHOUT it
// That last unit's ELECTION list (P14) is PF,QL,TA,YA,YC,YE — it names all three — so a rule
// keyed on P14 would have surcharged it and been wrong. An election is not an application.
//
// Three answers, never two: "surcharge", "none", or "unknown". The database list is a subset
// of the election list (measured across both books: P14 is a strict superset of P15 f[38]),
// so an ELECTION list that names none of QL/YE/YC settles it as "none"; one that does name
// them settles nothing. A missing list is UNKNOWN, not "none".
export const RDF_SURCHARGE_OPTIONS = Object.freeze(["QL", "YE", "YC"]);
// Option strings arrive in three shapes ("QL,TA,YA", "EU, TA, YA", "EU01,QL"); split on
// non-letters exactly as parseOptionCodes does in App.jsx. null/undefined -> null (unknown);
// an empty string is a KNOWN empty list.
export function optionCodesOf(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return v.map((x) => String(x).toUpperCase().trim()).filter(Boolean);
  return String(v).toUpperCase().split(/[^A-Z]+/).filter(Boolean);
}
export function rdfSurchargeBranch({ yieldOptions = null, electedOptions = null } = {}) {
  const y = optionCodesOf(yieldOptions);
  if (y) return y.some((c) => RDF_SURCHARGE_OPTIONS.includes(c)) ? "surcharge" : "none";
  const e = optionCodesOf(electedOptions);
  if (e && !e.some((c) => RDF_SURCHARGE_OPTIONS.includes(c))) return "none";
  return "unknown";
}
// The cubic term itself. Exactly 1 at or below 0.85, so the branch never matters there.
export function rdfSurcharge(eff) {
  return 1 + R(Math.pow(Math.min((Math.max(0.85, eff) - 0.85) / 0.15, 1), 3), 7) * 0.05;
}

// §5 revenue add-on. Mean/std scale off the approved yield at the EFFECTIVE level; only the
// guarantee-to-mean RATIO matters, so this equals scaling off the rate yield at the elected
// level. `cl` here is therefore the EFFECTIVE coverage level.
function addOn({ CMN, CSD, DRAWS }, comboKey, drawKey, rateY, guarY, cl, price, vol, lookupRate, hpe) {
  const cmn = CMN[comboKey], csd = CSD[comboKey], draws = DRAWS[drawKey];
  // NEVER silently return 0 here. The add-on is ~25% of the premium, so a missing table
  // would quietly UNDER-price the grower rather than fail — the worst possible failure mode
  // for an agency. Throw so a bad table set is impossible to ship unnoticed.
  if (!cmn || !csd) throw new Error(`MPCI: no combo revenue factor for key "${comboKey}"`);
  if (!draws) throw new Error(`MPCI: no beta draws for key "${drawKey}"`);
  const i = comboIdx(lookupRate), mn = cmn[i], sd = csd[i];
  if (mn == null || sd == null) throw new Error(`MPCI: combo table "${comboKey}" has no entry at index ${i} (lookupRate ${lookupRate})`);
  const AM = rateY * mn / 100, AS = rateY * sd / 100, g = guarY * cl, Ln = R(Math.log(price) - vol * vol / 2, 8);
  let sYP = 0, sRP = 0;
  for (let k = 0; k < draws.length; k++) {
    const yd = draws[k][0], pd = draws[k][1];
    const sy = R(Math.max(0, R(yd * AS + AM, 12)), 12), hp = R(Math.min(2 * price, R(Math.exp(pd * vol + Ln), 12)), 12);
    sYP += R(Math.max(0, R(g - sy, 12)), 12);
    const rg = hpe ? g * price : g * Math.max(price, hp);
    sRP += R(Math.max(0, R(rg - sy * hp, 12)), 12);
  }
  return R(R((sRP / 500) / (g * price), 8) - R((sYP / 500) / g, 8), 8);
}

// tables = { base:{refAmt,refRate,expo,fixed,disc}, cov:{ "0.80":{rdf,ur,er}, … },
//            disc:[{cl,lo,hi,ou,bu,eu}], sub:{ "0.80":pct }, CMN, CSD, DRAWS, comboKey, drawKey }
// BFR (Beginning Farmer & Rancher) adds TEN PERCENTAGE POINTS of premium subsidy and
// leaves the gross premium alone. Derived from R&H's Coverage Analyzer run twice on
// ATWATER FARMS (MP-865508) on 2026-08-04 — same gross $11,884 both times, producer
// premium $2,377 (80%) without and $1,189 (90%) with. It applies to SCO and ECO on the
// same terms: $4,999→$2,500 and $2,208→$1,104, all three reconciling to the penny.
// NOT read from the ADM — the subsidy table carries no BFR row — so this constant is
// evidence-backed rather than derived, and the fixture in validate_nau.mjs is what keeps
// it honest. BFR's OTHER effect — the YA plug moving from 60% to 80% of the T-yield — is
// on the yield side, in computeApprovedYield2027, not here.
export const BFR_SUBSIDY_BONUS = 0.10;
// The OBBBA addition, for the first TWO crop years only. It EXPIRES, which is why
// nothing derives it from a prior year's policy without saying so — see
// src/bfrStatus.js. Total bonus, not an increment on top of the 10.
export const BFR_FIRST_YEARS_BONUS = 0.15;

// ⚠ THE SCHEDULE IS GRADUATED — FOUR STEPS, NOT TWO. Corrected 2026-09-30, and the
// two-tier model above was an INFERENCE that fitted every case we had.
//
// RMA, after OBBBA (enacted 2025-07-04): a beginning farmer receives an additional
// **15 points for the 1st–2nd crop years, 13 for the 3rd, 11 for the 4th, and 10 for
// the 5th–10th**, the qualifying window itself having gone from 5 crop years to 10.
// Sourced from RMA's own BFR/VFR guidance and MGR-25-006, not back-solved.
//
// HOW THE GAP SURVIVED MONTHS OF VALIDATION: the only BFR fixture is ATWATER FARMS at
// coverage level 0.50, a 5th-year-or-later grower, so +10 fitted it to the penny — and
// at 50% a +10 bonus happens to HALVE the producer premium exactly ($4.54 → $2.27),
// which is unusually clean arithmetic and reads as strong confirmation. It pinned the
// MECHANISM and never the SCHEDULE. Nothing in the fixture set was in year 3 or 4, so
// the two interior steps were invisible; +15 and +10 fitted everything, and the middle
// was filled in by assumption. Rule zero: an inference that fits the data is not
// evidence.
//
// WHAT EXPOSED IT: FIRE CREEK FARMS MP-0926271 CY2027 Livingston. R&H's Coverage
// Analyzer states "Years of BFR Benefits Received - 3 Years: **11%**" — 3 years already
// received, so the quote year is their 4th — and its own figures give
// 3,963 ÷ 4,833 = **82.0%** against an EU-80 base of 71%, i.e. +11 exactly. This engine
// billed +10 and overstated the producer premium on every line of that policy.
// Measured across the live CY2026 book, the SCO/ECO subsidy ratios cluster TIGHTLY and
// SEPARATELY at 0.90 (4 policies) and **0.91 (5 policies)** — the book was showing the
// 4th-year step the whole time; a 2-point tolerance rounded it away.
export const BFR_BONUS_BY_BENEFIT_YEAR = Object.freeze({
  1: 0.15, 2: 0.15, 3: 0.13, 4: 0.11,
  5: 0.10, 6: 0.10, 7: 0.10, 8: 0.10, 9: 0.10, 10: 0.10,
});
// Past the 10th crop year a grower is no longer a beginning farmer at all.
export const BFR_LAST_BENEFIT_YEAR = 10;

// `benefitYear` is the crop year of BFR benefit being QUOTED (1 = first). A year we
// cannot establish falls back to the DURABLE +10 rather than an interior step: the two
// interior tiers are worth MORE, and quoting a bonus a grower may not have understates
// a premium on a document they sign. Same direction as bfrQuoteSeed's refusal to carry
// the +15 forward.
export function bfrBonusForYear(bfr, benefitYear) {
  if (!bfr) return 0;
  const y = Number(benefitYear);
  if (!Number.isInteger(y) || y < 1) return BFR_SUBSIDY_BONUS;
  if (y > BFR_LAST_BENEFIT_YEAR) return 0;
  return BFR_BONUS_BY_BENEFIT_YEAR[y];
}

// One place decides which bonus applies, so a caller cannot pass 0.15 where the
// grower is past their second year by writing a number instead of a fact.
//
// ⚠ THE BOOLEAN CANNOT EXPRESS THE INTERIOR STEPS, and it is kept because every
// existing caller and fixture passes it — changing its meaning would silently re-rate
// the whole book. `firstTwoYears` maps to year 1 and its absence to the durable +10,
// which is EXACTLY what this function did before the schedule was corrected, so no
// caller moves until it opts in by passing `benefitYear`. A caller that knows the year
// should pass it; one that does not keeps today's conservative answer.
export function bfrBonusFor(bfr, firstTwoYears, benefitYear = null) {
  if (!bfr) return 0;
  if (benefitYear != null) return bfrBonusForYear(true, benefitYear);
  return firstTwoYears ? BFR_FIRST_YEARS_BONUS : BFR_SUBSIDY_BONUS;
}

// Subsidy dollars, and the producer pays the remainder. Two things here are load-bearing
// and neither is guessable — both were solved against 12 figures across four R&H
// Illustrates (Niagara 524ac, Orleans 400ac, Orleans 100ac, and the BFR run), on RP, SCO
// and ECO alike:
//
//   1. The subsidy is ROUNDED, not truncated. Truncating fits 9 of the 12 — it happens to
//      fit the BFR case — but puts the producer $1 high on Orleans at both acreages.
//   2. Under BFR the base subsidy and the 10-point bonus are rounded SEPARATELY and then
//      added. Rounding one combined 90% rate fits 10 of 12 and misses both BFR lines:
//      round(11884 x 0.9) = 10696, where R&H shows 10695 = round(11884 x .8) +
//      round(11884 x .1) = 9507 + 1188. Only the split rule fits all 12.
//
// Get this wrong and the error is exactly $1 per line, which is precisely the kind of
// thing that reads as a rounding quibble and is actually a rule.
export function subsidyOf(grossTotal, baseRate, bfr = false, firstTwoYears = false, benefitYear = null) {
  const bonus = bfrBonusFor(bfr, firstTwoYears, benefitYear);
  return Math.round(grossTotal * baseRate) + (bonus ? Math.round(grossTotal * bonus) : 0);
}

// MC (Multi-County Enterprise Unit) is an ELIGIBILITY election, not a rating one — Kevin,
// 2026-08-05. A county that cannot meet the enterprise-unit test on its own borrows
// qualifying acreage from another county the grower farms, so it can be written EU at all.
// Once it qualifies, it is rated on ITS OWN acres like any other EU.
//
// That is exactly what the numbers show, which is why this is stated as fact rather than
// inference: R&H ran Atwater's Orleans county at 400 acres and again at 100, MC elected
// both times, and both match this engine on the county's own acreage to the dollar (gross
// 7,157 and 1,806). Combining with Niagara's 524 gives 7,138 and 1,789, which R&H does not
// show, and the illustrations print "Total Unit Acres EU" as 400.0 and 100.0. The pair
// straddles a discount band edge, so a combining rule could not have hidden.
//
// CONSEQUENCE FOR QUOTING: MC never changes the arithmetic, so there is nothing to model
// here. What it changes is whether EU may be OFFERED on a small county — and the value of
// it is the EU-versus-BU/OU spread on that county, which this engine already prices by
// quoting the unit structure the grower is eligible for.
// `optionRate` is an A01060 Option Level "C" multiplier on the COUNTY BASE RATE — the only
// place in this chain such an option can be applied. Defaults to 1, so every existing caller
// is bit-identical. Today only two wheat options carry one: PF (1.03, prevented-planting
// buy-up) and HF (0.87–0.96 by county, hail & fire exclusion, which the agency doesn't write).
// `priceElectionPct` is the share of the published price the grower elected — 1.00 on a
// normal buy-up, 0.55 on CAT. It scales the liability, and the premium with it.
//
// `cat` is Catastrophic coverage (50% yield / 55% price). Its premium is 100% subsidized:
// the producer pays a flat administrative FEE, not a rated premium (Kevin, 2026-08-06).
// So the rated producer premium is forced to zero rather than run through the buy-up
// subsidy table, which has no CAT row and would otherwise report a large premium the
// grower will never be billed. THE FEE ITSELF IS MODELLED — see `adminFee` below,
// which is the whole cost of a CAT policy and the only thing the grower is billed.
export function quoteMPCI({ tables, aph, cl, acres, unit = "BU", price, vol = 0.20, plan = "RP",
                            rateYield = null, adjustedYield = null, bfr = false, bfrFirstTwoYears = false, bfrBenefitYear = null, optionRate = 1,
                            ppFactor = null, priceElectionPct = 1, cat = false,
                            yieldOptions = null, electedOptions = null }) {
  const { base: b, cov: cvT, disc: dtbl, sub: SUB } = tables;
  if (!b || !cvT || !cvT[(+cl).toFixed(2)]) return null;
  const rateY = (rateYield != null && isFinite(+rateYield) && +rateYield > 0) ? +rateYield : aph;
  // ADJUSTED defaults to APPROVED, *not* to the rate yield. adjusted = APH without TA/YE but
  // WITH YA — so with no trend adjustment elected, adjusted == approved and eff == cl. The AIP
  // populates it (P15 f43) precisely when TA is elected. Defaulting it to the rate yield instead
  // silently invents a trend adjustment for every YA-only grower and over-rates them (~12%):
  // Bradford YA-only, rate 50 / approved 53 -> eff would come out 0.74 instead of 0.70.
  const adjY  = (adjustedYield != null && isFinite(+adjustedYield) && +adjustedYield > 0) ? +adjustedYield : aph;

  // §3 base rate — off the RATE yield. Yield ratio cupped at 0.50 / capped at 1.50.
  const yr = Math.min(1.5, Math.max(0.5, R(rateY / b.refAmt, 2)));
  const baseRate = R(R(b.refRate * yr ** b.expo + b.fixed, 8) * (optionRate || 1), 8);

  // §13 effective coverage level — off the ADJUSTED yield. ROUNDED TO 2 DECIMALS; the
  // rounding is load-bearing (carrying full precision leaves a ~1% error).
  const lv = Object.keys(cvT).map(Number).sort((a, b2) => a - b2);
  const eff = R((+cl) * aph / adjY, 2);
  const top = lv[lv.length - 1];
  const capExceeded = eff > top;   // above the top ADM level: §14 extrapolates, §20 caps (both below)
  const bd = effBounds(lv, eff), F = (x, f) => cvT[x.toFixed(2)][f];
  const residKey = unit === "EU" ? "er" : "ur";

  // §14 rate differential: linear on the 5% grid (extrapolated off the top two levels above the
  // top), times the cubic surcharge ONLY on the branch RMA gives it — see rdfSurchargeBranch.
  // The RDF field rounds to 9 decimals.
  const rdfLinear = effInterp(F(bd.fl, "rdf"), F(bd.up, "rdf"), F(bd.lo, "rdf"), eff, bd.fl, 9);
  const surTerm = rdfSurcharge(eff);
  const rdfBranch = surTerm === 1 ? "not-applicable" : rdfSurchargeBranch({ yieldOptions, electedOptions });
  const rdfFor = (withSur) => (withSur ? R(surTerm * rdfLinear, 9) : rdfLinear);
  // §15 unit residual, capped at the max across coverage levels
  const capR = Math.max(...lv.map(x => F(x, residKey)));
  const resid = Math.min(capR, effInterp(F(bd.fl, residKey), F(bd.up, residKey), F(bd.lo, residKey), eff, bd.fl, 4));

  // §16 unit discount, interpolated across coverage levels within the acreage band
  const dlv = [...new Set((dtbl || []).map(d => d.cl))].sort((a, b2) => a - b2);
  const db = effBounds(dlv, eff);
  const uDisc = unit === "OU" ? 1.0 : Math.min(1.0, effInterp(
    discFactor(unit, db.fl, acres, dtbl), discFactor(unit, db.up, acres, dtbl), discFactor(unit, db.lo, acres, dtbl), eff, db.fl, 4));

  // §5 revenue add-on. It does not read the RDF, so it is the same on either §14 branch; only
  // its 1%-of-base-premium-rate floor does, and that is applied per branch below.
  const revAdj = unit === "OU" ? 1.0 : discFactor(unit, 0.65, acres, dtbl);
  const lookupRate = R(Math.min(baseRate, 0.9999) * revAdj, 4);
  const addSim = plan === "YP" ? 0
    : addOn(tables, tables.comboKey, tables.drawKey, aph, aph, eff, price, vol, lookupRate, plan === "HPE");

  const guarBu = R(aph * cl, 1);                 // guarantee: approved yield × the ELECTED level
  const pePct = (priceElectionPct != null && isFinite(+priceElectionPct) && +priceElectionPct > 0 && +priceElectionPct <= 1) ? +priceElectionPct : 1;
  const liabPerAc = guarBu * price * pePct;
  // PREVENTED PLANTING. RMA insures PP acres at a published FRACTION of the same per-acre
  // guarantee — not as a separate guarantee with its own rounding. Confirmed by the shape of
  // the AIP's own blended liability, liability = fullGuarPerAc × (planted + pp × F): solving
  // 2026 units for F reproduces RMA's published factors (corn 0.55 on 32 of 54 units,
  // soybeans 0.60 on 15 of 26; the rest scatter because pp_acres is a small denominator and
  // the stored summary APH carries known drift). Base premium already covers PP — only the
  // PF buy-up costs more, and that arrives via optionRate above, never here.
  const ppF = (ppFactor != null && isFinite(+ppFactor) && +ppFactor > 0) ? +ppFactor : null;
  const ppLiabPerAc = ppF != null ? R(liabPerAc * ppF, 2) : null;
  // §1 + §9: the premium is priced on the WHOLE-DOLLAR liability. Premium Total Guarantee
  // Amount rounds to cents, Premium Liability Amount rounds to whole dollars, and Preliminary
  // Total Premium = Round(Premium Liability Amount × Premium Rate, 0). Multiplying the rate by
  // the UNROUNDED liability instead lands a dollar off whenever the product sits near .5 —
  // Woodlawn Livingston 75%: 36,250.27 × 0.11689575 = 4,237.50 -> 4,238, where RMA prices
  // 36,250 × 0.11689575 = 4,237.47 -> 4,237, and NAU's quote reads $9.24/ac, not $9.25.
  const liabilityTotal = Math.round(R(liabPerAc * acres, 2));
  const sub = cat ? 1 : Math.min(1, ((SUB || {})[(+cl).toFixed(2)] ?? 0) + bfrBonusFor(bfr, bfrFirstTwoYears, bfrBenefitYear));

  // Everything downstream of the base premium rate, for one §14 branch.
  const priceBranch = (withSur) => {
    const rdf = rdfFor(withSur);
    const bprLinear = R(baseRate * rdf * resid, 8);
    // §20 — ONLY above the top ADM level. A marginal-rate CAP on the base premium rate: the
    // part of the liability the top offered level would have covered keeps the top level's
    // factors, and the excess can never be charged more than a 100% rate. RMA P11-1 RY2027 §20:
    //   UL    = Round(Round(cl / eff, 10) × PL, 0)                          (whole dollars)
    //   MCLAF = Round(Round(1/CYBaseRate,8) − Round(UL/(CYBaseRate × PL),8)
    //               + Round(Round(BaseRDF × BaseResid × BaseDisc × UL, 8) / PL, 8), 8)
    //   MRAF  = Round(MCLAF / Round(RDF × Resid × UnitDiscount, 12), 8)
    //   CY Base Premium Rate = Round(CYBaseRate × RDF × Resid, 8) × MIN(MRAF, 1.00)
    // The "Base" factors are the TOP level's. The base unit discount is 1.00 when every acre
    // of the unit is prevented planted — a quote rates planted acres, so it reads the table.
    // On every validated case MRAF is well above 1 (Kirby 80% 1.89, Sullivan 1.37), so the
    // cap does not bind there; it is implemented because it is the rule, and it binds for a
    // high enough effective level on a cheap enough county.
    let mraf = null, bpr = bprLinear;
    if (capExceeded && liabilityTotal > 0 && baseRate > 0) {
      const PL = liabilityTotal;
      const UL = Math.round(R((+cl) / eff, 10) * PL);
      const baseDisc = unit === "OU" ? 1.0 : discFactor(unit, top, acres, dtbl);
      const mclaf = R(R(1 / baseRate, 8) - R(UL / (baseRate * PL), 8)
        + R(R(F(top, "rdf") * F(top, residKey) * baseDisc * UL, 8) / PL, 8), 8);
      mraf = R(mclaf / R(rdf * resid * uDisc, 12), 8);
      bpr = R(bprLinear * Math.min(mraf, 1), 8);
    }
    // §5 floor: the RP add-on is at least 1% of the (final) base premium rate.
    const add = plan === "YP" ? 0 : Math.max(addSim, R(0.01 * bpr, 8));
    const premRate = Math.min(0.999, R(bpr * uDisc + add, 8));
    const grossTotal = Math.round(liabilityTotal * premRate);
    const subsidyTotal = cat ? grossTotal : subsidyOf(grossTotal, (SUB || {})[(+cl).toFixed(2)] ?? 0, bfr, bfrFirstTwoYears, bfrBenefitYear);
    const netTotal = grossTotal - subsidyTotal;      // CAT: zero — the grower pays a flat fee instead
    return { rdf, bpr, mraf, add, premRate, grossTotal, netTotal,
             grossAcre: R(grossTotal / acres, 2), netAcre: R(netTotal / acres, 2) };
  };
  // "unknown" prices on the surcharge branch — the higher of the two, and what this engine
  // charged everyone before BUG-095 — AND carries the TA-alone figure beside it, so a surface
  // can state how far the answer could move rather than present one side as settled.
  const primary = priceBranch(rdfBranch === "surcharge" || rdfBranch === "unknown");
  const alt = rdfBranch === "unknown" ? priceBranch(false) : null;
  const { rdf, mraf, add, premRate, grossTotal, netTotal } = primary;
  const grossPerAc = premRate * liabPerAc;
  // ── ADMINISTRATIVE FEE ──────────────────────────────────────────────────────
  // Reported, deliberately NOT added to netTotal. The fee is charged once per crop
  // per COUNTY, and a quote row here is one county × practice × unit structure — a
  // grower comparing BU against EU in the same county sees two rows and owes ONE
  // fee. Folding it in would count it as many times as the screen has rows.
  //
  // It matters most exactly where the rated premium says nothing: CAT premium is
  // 100% subsidised, so netTotal is $0 and the quote reads "free" when the grower
  // will be billed $655 for that crop in that county. That is the whole cost of
  // CAT and it was missing from the number a grower sees.
  //
  // $655 CAT / $30 buy-up, per crop per county. Sourced: RMA MGR-19-006 raised the
  // CAT fee from $300 under the 2018 Farm Act, and the $30 buy-up fee prints in the
  // FEES column of the R&H Schedules of Insurance in this book — not inferred.
  const adminFee = cat ? 655 : 30;
  return { premRate, baseRate, rdf, resid, uDisc, add, guarBu, liabPerAc, grossPerAc, grossTotal,
           adminFee, adminFeeBasis: "per crop per county — count once, not once per quote row",
           producerCostWithFee: netTotal + adminFee,
           subsidy: sub, netTotal, grossAcre: primary.grossAcre, netAcre: primary.netAcre,
           liabilityTotal, effCl: eff, capExceeded,
           // §14 branch actually used, and — only when it could not be determined — the figure
           // on the other branch. "not-applicable" means eff <= 0.85, where both are identical.
           rdfBranch, mraf, bpr: primary.bpr,     // bpr == the AIP's P11 Base Premium Rate (f110)
           rdfBranchAlt: alt ? { branch: "none", premRate: alt.premRate, grossTotal: alt.grossTotal,
                                 netTotal: alt.netTotal, grossAcre: alt.grossAcre, netAcre: alt.netAcre } : null,
           priceElectionPct: pePct, cat: !!cat,
           ppFactor: ppF, ppGuarBu: ppF != null ? R(guarBu * ppF, 1) : null, ppLiabPerAc,
           ppLiabilityTotal: ppLiabPerAc != null ? Math.round(ppLiabPerAc * acres) : null };
}
